import classNames from "classnames"

import LayoutItem from "../Layouts/LayoutItem"

import Link from "@components/elements/Link"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { trackSegmentEvent } from "@utils/analytics"
import {
  getAccentColor,
  getBackgroundColorClass,
  getTextColorClass,
} from "@utils/V2/color"
import React, { useCallback } from "react"

type CTACardProps = {
  ctaLink: string | YocoCom.YocoLink | undefined
  ctaText: string
  onClick?: VoidFunction
  backgroundColor: Color
  mobileGridOrder?: number
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const CTACard = ({
  ctaLink,
  ctaText,
  onClick,
  trackingEvent,
  backgroundColor,
  trackingEventKey,
  trackingEventValue,
  mobileGridOrder,
}: CTACardProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, link?: string | YocoCom.YocoLink) => {
      const linkUrl = typeof link === "string" ? link : link?.url

      if (trackingEvent) {
        const event =
          trackingEventKey && trackingEventValue
            ? { [trackingEventKey]: trackingEventValue }
            : undefined

        trackSegmentEvent(trackingEvent, event)
      }

      if (onClick) {
        onClick()
      } else if (linkUrl?.startsWith("#")) {
        e.preventDefault()
        const [scrollId, clickId] = linkUrl.includes("@")
          ? linkUrl.split("@")
          : [linkUrl, null]

        if (clickId) {
          document.getElementById(clickId)?.click()
        }

        if (scrollId !== "#") {
          document
            .querySelector(scrollId)
            ?.scrollIntoView({ behavior: "smooth" })
        }
      }
    },
    [trackingEvent, onClick, trackingEventKey, trackingEventValue]
  )

  const icon = (
    <ArrowIcon
      className={classNames(
        getTextColorClass(getAccentColor(backgroundColor)),
        "transition-all duration-300 ease-linear sm:h-40 sm:w-40 md:h-[58px] md:w-[58px]"
      )}
    />
  )

  return (
    <>
      {/* Desktop */}
      <LayoutItem
        as={Link}
        animateOnScroll
        // Mobile grid order is not needed for desktop
        link={ctaLink || "#"}
        onClick={(e) => handleClick(e, ctaLink)}
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "group col-span-2 hidden min-h-[389px] flex-col justify-between rounded-20 p-40 sm:flex lg:col-span-4"
        )}
      >
        {icon}
        <Typography
          text={ctaText}
          size="lead-lg"
          font="grotesk"
          weight="medium"
          color={getAccentColor(backgroundColor)}
          className="transition-all duration-300 ease-linear"
        />
      </LayoutItem>

      {/* Mobile */}
      <LayoutItem
        as={PillButton}
        icon={icon}
        size="medium"
        style="solid"
        text={ctaText}
        linkUrl={ctaLink}
        fontSize="body-sm"
        color={backgroundColor}
        trackingEvent={trackingEvent}
        trackingEventKey={trackingEventKey}
        trackingEventValue={trackingEventValue}
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "col-span-full sm:hidden"
        )}
        mobileGridOrder={mobileGridOrder}
      />
    </>
  )
}

export default CTACard
